import {
  Alert,
  AlertDescription,
  AlertIcon,
  Avatar,
  Badge,
  Box,
  Button,
  ButtonGroup,
  Flex,
  Heading,
  Link,
  Spinner,
  Stack,
  Text,
  Textarea,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  getAdminSupportConversation,
  getAdminSupportConversations,
  replyAdminSupportConversation,
  updateAdminSupportConversationStatus,
} from "../../../services/adminApi";

const FILTERS = [
  { value: "open", label: "Open", countKey: "open" },
  { value: "closed", label: "Closed", countKey: "closed" },
  { value: "all", label: "All" },
];

const PLANS = {
  lifetime: { label: "Premium lifetime", colorScheme: "purple" },
  premium: { label: "Premium", colorScheme: "green" },
  credited: { label: "Premium (credited)", colorScheme: "green" },
  beta: { label: "Beta user", colorScheme: "blue" },
  free: { label: "Free", colorScheme: "gray" },
};

const PLATFORMS = {
  revenue_cat: "via the App Store / Play Store",
  stripe: "via Stripe",
};

const dateTimeLabel = (value) =>
  value
    ? new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : "";

const dateLabel = (value) =>
  value
    ? new Intl.DateTimeFormat(undefined, { dateStyle: "long" }).format(
        new Date(value)
      )
    : "Unknown";

const displayName = (conversation) => {
  const user = conversation.user;
  if (!user) {
    return conversation.email;
  }

  return `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email;
};

const needsReply = (conversation) =>
  conversation.status === "open" && conversation.lastMessageFrom === "user";

const ReaderCard = ({ conversation }) => {
  const user = conversation.user;
  const cardBg = useColorModeValue("gray.50", "whiteAlpha.100");

  if (!user) {
    return (
      <Box bg={cardBg} borderRadius="md" p="4">
        <Flex align="center" gap="3">
          <Avatar size="md" name={conversation.email} />
          <Box>
            <Text fontWeight="semibold">{conversation.email}</Text>
            <Badge>Guest · not signed in</Badge>
          </Box>
        </Flex>
      </Box>
    );
  }

  const plan = PLANS[user.premiumStatus] || PLANS.free;

  return (
    <Box bg={cardBg} borderRadius="md" p="4">
      <Flex align="center" gap="3" wrap="wrap">
        <Avatar
          size="lg"
          src={user.profilePicture}
          name={displayName(conversation)}
        />
        <Box flex="1" minW="200px">
          <Text fontWeight="semibold" fontSize="lg">
            {displayName(conversation)}
            {user.userHandle && (
              <Text as="span" color="gray.500" fontWeight="normal" ml="2">
                @{user.userHandle}
              </Text>
            )}
          </Text>
          <Link href={`mailto:${user.email}`} color="blue.300" fontSize="sm">
            {user.email}
          </Link>
          <Flex align="center" gap="2" mt="1" wrap="wrap">
            <Badge colorScheme={plan.colorScheme}>{plan.label}</Badge>
            {user.premiumStatus === "premium" &&
              PLATFORMS[user.subscriptionPlatform] && (
                <Text fontSize="xs" color="gray.500">
                  {PLATFORMS[user.subscriptionPlatform]}
                </Text>
              )}
          </Flex>
          <Text fontSize="sm" color="gray.500" mt="1">
            Joined Redwing on {dateLabel(user.joinedAt)}
          </Text>
        </Box>
      </Flex>
    </Box>
  );
};

const ConversationDetail = ({ conversationId }) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [reply, setReply] = useState("");
  const teamBg = useColorModeValue("red.50", "rgba(237, 29, 36, 0.15)");
  const readerBg = useColorModeValue("gray.100", "whiteAlpha.200");

  const conversationQuery = useQuery(
    ["adminSupportConversation", conversationId],
    () => getAdminSupportConversation(conversationId),
    { refetchOnWindowFocus: false }
  );

  useEffect(() => {
    setReply("");
  }, [conversationId]);

  const refresh = (response) => {
    queryClient.setQueryData(
      ["adminSupportConversation", conversationId],
      response
    );
    queryClient.invalidateQueries(["adminSupportConversations"]);
  };

  const showError = (title, error) =>
    toast({
      title,
      description:
        error.response?.data?.message || "Please try again in a moment.",
      status: "error",
      duration: 6000,
      isClosable: true,
    });

  const replyMutation = useMutation(
    ({ message, close }) =>
      replyAdminSupportConversation(conversationId, message, close),
    {
      onSuccess: (response, { close }) => {
        setReply("");
        refresh(response);
        toast({
          title: close ? "Reply sent and conversation closed." : "Reply sent.",
          description:
            response.emailSent === false
              ? "Saved, but the email could not be delivered. Check the mail logs."
              : `Emailed to ${response.data.email}.`,
          status: response.emailSent === false ? "warning" : "success",
          duration: 5000,
          isClosable: true,
        });
      },
      onError: (error) => showError("Unable to send reply.", error),
    }
  );

  const statusMutation = useMutation(
    (status) => updateAdminSupportConversationStatus(conversationId, status),
    {
      onSuccess: (response) => {
        refresh(response);
      },
      onError: (error) => showError("Unable to update conversation.", error),
    }
  );

  if (conversationQuery.isLoading) {
    return (
      <Flex justify="center" py="12">
        <Spinner />
      </Flex>
    );
  }

  if (conversationQuery.isError) {
    return (
      <Alert status="error">
        <AlertIcon />
        <AlertDescription>
          This conversation could not be loaded.
        </AlertDescription>
      </Alert>
    );
  }

  const conversation = conversationQuery.data.data;
  const isOpen = conversation.status === "open";
  const sendReply = (close) => {
    const message = reply.trim();
    if (message) {
      replyMutation.mutate({ message, close });
    }
  };

  return (
    <Stack spacing="4">
      <Flex align="center" gap="3" wrap="wrap">
        <Heading as="h3" size="md">
          Conversation #{conversation.id}
        </Heading>
        <Badge colorScheme={isOpen ? "green" : "gray"}>
          {conversation.status}
        </Badge>
        <Button
          ml="auto"
          size="sm"
          variant="outline"
          isLoading={statusMutation.isLoading}
          onClick={() => statusMutation.mutate(isOpen ? "closed" : "open")}
        >
          {isOpen ? "Close conversation" : "Reopen"}
        </Button>
      </Flex>

      <ReaderCard conversation={conversation} />

      {conversation.pageUrl && (
        <Text fontSize="xs" color="gray.500" wordBreak="break-all">
          Sent from{" "}
          <Link href={conversation.pageUrl} isExternal color="blue.300">
            {conversation.pageUrl}
          </Link>
        </Text>
      )}

      <Stack spacing="3">
        {conversation.messages.map((message) => {
          const fromTeam = message.sender === "admin";

          return (
            <Box
              key={message.id}
              alignSelf={fromTeam ? "flex-end" : "flex-start"}
              maxW="85%"
            >
              <Box
                bg={fromTeam ? teamBg : readerBg}
                borderRadius="lg"
                px="4"
                py="3"
                whiteSpace="pre-wrap"
                wordBreak="break-word"
              >
                {message.body}
              </Box>
              <Text
                fontSize="xs"
                color="gray.500"
                mt="1"
                textAlign={fromTeam ? "right" : "left"}
              >
                {fromTeam ? "Redwing" : displayName(conversation)} ·{" "}
                {dateTimeLabel(message.createdAt)}
              </Text>
            </Box>
          );
        })}
      </Stack>

      <Box>
        <Textarea
          aria-label="Reply"
          placeholder={`Reply to ${conversation.email}…`}
          rows={4}
          maxLength={5000}
          value={reply}
          onChange={(event) => setReply(event.target.value)}
        />
        <Flex mt="2" gap="2" justify="flex-end" wrap="wrap">
          <Text fontSize="xs" color="gray.500" mr="auto" alignSelf="center">
            Your reply is emailed to the reader
            {conversation.user ? " and shown in their chat." : "."}
          </Text>
          <Button
            variant="outline"
            onClick={() => sendReply(true)}
            isDisabled={!reply.trim() || replyMutation.isLoading}
          >
            Send &amp; close
          </Button>
          <Button
            colorScheme="red"
            onClick={() => sendReply(false)}
            isLoading={
              replyMutation.isLoading && !replyMutation.variables?.close
            }
            isDisabled={!reply.trim()}
          >
            Send reply
          </Button>
        </Flex>
      </Box>
    </Stack>
  );
};

const Support = ({ initialConversationId = null }) => {
  const [status, setStatus] = useState(initialConversationId ? "all" : "open");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState(initialConversationId);
  const selectedBg = useColorModeValue("gray.100", "whiteAlpha.200");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.300");

  const listQuery = useQuery(
    ["adminSupportConversations", status, page],
    () => getAdminSupportConversations(status, page),
    { keepPreviousData: true, refetchInterval: 60000 }
  );

  const conversations = listQuery.data?.data || [];
  const counts = listQuery.data?.counts || {};
  const lastPage = listQuery.data?.meta?.lastPage || 1;

  const changeFilter = (value) => {
    setStatus(value);
    setPage(1);
  };

  return (
    <>
      <Flex align="center" gap="3" mb="5" wrap="wrap">
        <ButtonGroup size="sm" isAttached variant="outline">
          {FILTERS.map((filter) => (
            <Button
              key={filter.value}
              isActive={status === filter.value}
              onClick={() => changeFilter(filter.value)}
            >
              {filter.label}
              {filter.countKey && counts[filter.countKey] !== undefined
                ? ` (${counts[filter.countKey]})`
                : ""}
            </Button>
          ))}
        </ButtonGroup>
        {counts.awaitingReply > 0 && (
          <Badge colorScheme="red">{counts.awaitingReply} awaiting reply</Badge>
        )}
      </Flex>

      {listQuery.isLoading && (
        <Flex justify="center" py="12">
          <Spinner />
        </Flex>
      )}

      {listQuery.isError && (
        <Alert status="error">
          <AlertIcon />
          <AlertDescription>
            Support conversations could not be loaded. Please refresh and try
            again.
          </AlertDescription>
        </Alert>
      )}

      {listQuery.isSuccess && (
        <Flex gap="6" direction={{ base: "column", lg: "row" }} align="start">
          <Box w={{ base: "100%", lg: "380px" }} flexShrink="0">
            {conversations.length === 0 ? (
              <Alert status="success">
                <AlertIcon />
                <AlertDescription>
                  {status === "open"
                    ? "No open conversations. Inbox zero!"
                    : "No conversations here yet."}
                </AlertDescription>
              </Alert>
            ) : (
              <Stack
                spacing="0"
                borderWidth="1px"
                borderColor={borderColor}
                borderRadius="md"
                overflow="hidden"
              >
                {conversations.map((conversation) => (
                  <Box
                    as="button"
                    type="button"
                    key={conversation.id}
                    textAlign="left"
                    px="4"
                    py="3"
                    bg={selectedId === conversation.id ? selectedBg : undefined}
                    _hover={{ bg: selectedBg }}
                    borderBottomWidth="1px"
                    borderColor={borderColor}
                    onClick={() => setSelectedId(conversation.id)}
                  >
                    <Flex gap="3" align="start">
                      <Avatar
                        size="sm"
                        src={conversation.user?.profilePicture}
                        name={displayName(conversation)}
                      />
                      <Box flex="1" minW="0">
                        <Flex align="center" gap="2">
                          <Text fontWeight="semibold" noOfLines={1}>
                            {displayName(conversation)}
                          </Text>
                          {needsReply(conversation) && (
                            <Badge colorScheme="red" flexShrink="0">
                              Needs reply
                            </Badge>
                          )}
                          {conversation.status === "closed" && (
                            <Badge flexShrink="0">Closed</Badge>
                          )}
                        </Flex>
                        <Text fontSize="sm" color="gray.500" noOfLines={2}>
                          {conversation.lastMessageFrom === "admin" && "You: "}
                          {conversation.preview}
                        </Text>
                        <Text fontSize="xs" color="gray.500" mt="1">
                          {dateTimeLabel(conversation.lastMessageAt)}
                        </Text>
                      </Box>
                    </Flex>
                  </Box>
                ))}
              </Stack>
            )}

            {lastPage > 1 && (
              <Flex justify="space-between" align="center" mt="3">
                <Button
                  size="sm"
                  onClick={() => setPage((current) => current - 1)}
                  isDisabled={page <= 1}
                >
                  Previous
                </Button>
                <Text fontSize="sm" color="gray.500">
                  Page {page} of {lastPage}
                </Text>
                <Button
                  size="sm"
                  onClick={() => setPage((current) => current + 1)}
                  isDisabled={page >= lastPage}
                >
                  Next
                </Button>
              </Flex>
            )}
          </Box>

          <Box flex="1" minW="0" w="100%">
            {selectedId ? (
              <ConversationDetail conversationId={selectedId} />
            ) : (
              <Text color="gray.500" py="12" textAlign="center">
                Select a conversation to read and reply.
              </Text>
            )}
          </Box>
        </Flex>
      )}
    </>
  );
};

export default Support;
